import { Container, TextField, Button, Box, Paper, Typography, Autocomplete } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as endPoints from '../assets/js/endpoints';
import Loader from '../components/loader';
import { useEffect, useState, useContext } from 'react';
import { UserContext } from '../assets/contexts/userContext';
import { useNotification } from '../assets/contexts/notificationContext';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CreateReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    client_id: '',
    appointment_id: '',
    service_id: '',
    session_date: '',
    session_description: '',
    intervention: '',
    observations: '',
  });

  const loadAppointments = async clientId => {
    if (!clientId) {
      setAppointments([]);
      return;
    }
    try {
      let data = await endPoints.getAppointmentsByRole('client', clientId);
      let list = Array.isArray(data) ? data : data.data;
      if (user.hasRole('employee')) {
        list = list.filter(a => (a.users || []).some(u => u.id === user.id));
      }
      setAppointments(list);
    } catch (err) {
      if (err.response?.status === 404) {
        setAppointments([]);
        showNotification('este cliente no tiene citas', 'info');
      } else {
        showNotification('Error obteniendo citas', 'error');
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (user.hasRole('employee')) {
          const [c, s] = await Promise.all([
            endPoints.getClientsByEmployee(user.id),
            endPoints.getServicesByEmployee(user.id),
          ]);
          setClients(Array.isArray(c) ? c : c.data);
          setServices(Array.isArray(s) ? s : s.data);
        } else {
          const [c, s] = await Promise.all([
            endPoints.getAllClients(),
            endPoints.getAllServices(),
          ]);
          setClients(Array.isArray(c) ? c : c.data);
          setServices(Array.isArray(s) ? s : s.data);
        }

        if (id) {       
          const data = await endPoints.getReport(id);
          setForm({
            title: data.title || '',
            description: data.description || '',
            client_id: data.client_id || '',
            appointment_id: data.appointment_id || '',
            service_id: data.service_id || '',
            session_date: data.session_date || '',
            session_description: data.session_description || '',
            intervention: data.intervention || '',
            observations: data.observations || '',
          });
          // await loadAppointments(data.client_id);
        }
      } catch (_err) {
        showNotification('Error cargando datos', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, user.id]);

  useEffect(() => {
    loadAppointments(form.client_id);
  }, [form.client_id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'client_id' ? { appointment_id: '' } : {}),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        appointment_id: form.appointment_id || null,
        service_id: form.service_id || null,
      };
      if (id) {
        await endPoints.updateReport(id, data);
        showNotification('Informe actualizado', 'success');
      } else {
        await endPoints.createReport(data);
        showNotification('Informe creado', 'success');
      }
      user.hasRole('admin') ? navigate('/admin/manageReports') : navigate('/employee/reports');
    } catch (err) {
      const message = err.response?.data?.error || 'Error guardando informe';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <title>{id ? 'Actualizar Informe' : 'Crear Informe'} | CES</title>
      </Helmet>
      <Container sx={{ mt:5, pb:2, mb:2 }}>
        <Box sx={{ display:'flex', justifyContent:'flex-end', mb:2, maxWidth: 900, mx: 'auto'}}>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Volver</Button>
          </Box>
        <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto',}}>
          
          <Typography variant="h5" gutterBottom sx={{ mb: 3}}>
            {id ? 'Actualizar Informe' : 'Crear Informe'}
          </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display:'grid',
            gap:2,
            gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr' },
            maxWidth:750,
            mx:'auto',
          }}
        >
        <TextField label="Título" name="title" value={form.title} onChange={handleChange} required />
        <Autocomplete
          options={clients}
          value={clients.find(c => String(c.id) === String(form.client_id)) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({
              ...prev,
              client_id: newValue ? newValue.id : '',
              appointment_id: '',
            }));
          }}
          getOptionLabel={option => `${option.name} ${option.apellidos}`}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          renderInput={params => (
            <TextField {...params} label="Cliente" required />
          )}
        />
        <Autocomplete
          options={appointments}
          value={appointments.find(a => String(a.id) === String(form.appointment_id)) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({ ...prev, appointment_id: newValue ? newValue.id : '' }));
          }}
          getOptionLabel={option => `${option.date} ${option.time}`}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          renderInput={params => (
            <TextField {...params} label="Cita" sx={{ gridColumn: '1 / -1' }} placeholder="Sin cita" />
          )}
        />
        <Autocomplete
          options={services}
          value={services.find(s => String(s.id) === String(form.service_id)) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({ ...prev, service_id: newValue ? newValue.id : '' }));
          }}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          renderInput={params => (
            <TextField {...params} label="Servicio" placeholder="Sin servicio" />
          )}
        />
        <TextField label="Fecha" name="session_date" type="date" InputLabelProps={{ shrink:true }} value={form.session_date} onChange={handleChange} />
        <TextField label="Descripción de la sesión" name="session_description" value={form.session_description} onChange={handleChange} multiline rows={2} sx={{ gridColumn:'1 / -1' }} />
        <TextField label="Intervención / recomendaciones" name="intervention" value={form.intervention} onChange={handleChange} multiline rows={2} sx={{ gridColumn:'1 / -1' }} />
        <TextField label="Observaciones del profesional" name="observations" value={form.observations} onChange={handleChange} multiline rows={2} sx={{ gridColumn:'1 / -1' }} />
        <TextField
          label="Descripción"
          name="description"
          value={form.description}
          onChange={handleChange}
          multiline
          rows={3}
          sx={{ gridColumn:'1 / -1' }}
        />
        <Button type="submit" variant="contained" sx={{ gridColumn:'1 / -1', display: 'block', mx: 'auto' }}>
          {id ? 'Actualizar' : 'Crear'} Informe
        </Button>
        </Box>
        </Paper>
      </Container>
    </>
  );
};

export default CreateReport;
